import { useEffect, useRef, useState, type RefObject } from 'react';
import { Icon } from '../../design-system/icons';

/**
 * Défilement infini (ADR-096 §2) : une sentinelle sous les lignes demande la suite quand elle
 * approche de l'écran (marge 400 px), comme le v1 qui chargeait à 300 px du bas — mais avec une fin
 * de liste dite, une erreur réessayable et sans dépendre du thème. Sans `IntersectionObserver`
 * (navigateur ancien, test), un bouton « Charger la suite » prend le relais.
 */
export function InfiniteSentinel({ loadingMore, done, error, count, onMore }: { loadingMore: boolean; done: boolean; error: string | null; count: number; onMore: () => void }) {
  const sentinel = useRef<HTMLDivElement | null>(null);
  const supported = typeof IntersectionObserver !== 'undefined';
  const onMoreRef = useRef(onMore);
  onMoreRef.current = onMore;

  useEffect(() => {
    if (!supported || done || error !== null || sentinel.current === null) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onMoreRef.current();
    }, { rootMargin: '0px 0px 400px 0px' });
    observer.observe(sentinel.current);
    return () => observer.disconnect();
    // Réarmé après chaque page : si la sentinelle est encore visible, la suite est redemandée.
  }, [supported, done, error, count]);

  if (error !== null) {
    return (
      <div className="ay-collection__more ay-collection__more--error" role="alert">
        <span>La suite n’a pas pu être chargée : {error}</span>
        <button type="button" className="ay-button ay-button--secondary" onClick={onMore}>
          Réessayer
        </button>
      </div>
    );
  }
  if (done) {
    return (
      <div className="ay-collection__more ay-collection__more--end" role="status">
        {count.toLocaleString('fr')} élément{count > 1 ? 's' : ''}, fin de la liste
      </div>
    );
  }
  return (
    <div ref={sentinel} className="ay-collection__more" data-testid="collection-sentinel">
      {loadingMore ? (
        <span role="status" className="ay-collection__loading">
          <span className="ay-spinner" aria-hidden="true" />
          Chargement de la suite…
        </span>
      ) : (
        !supported && (
          <button type="button" className="ay-button ay-button--secondary" onClick={onMore}>
            Charger la suite
          </button>
        )
      )}
    </div>
  );
}

/** Retour en haut de la collection, au-delà d'un écran de défilement. */
export function BackToTop({ anchor }: { anchor: RefObject<HTMLElement | null> }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const target = anchor.current;
    if (target === null || typeof IntersectionObserver === 'undefined') return;
    // Visible quand le haut de la collection est sorti de l'écran par le haut.
    const observer = new IntersectionObserver(([entry]) => setVisible(entry !== undefined && !entry.isIntersecting && entry.boundingClientRect.top < 0));
    observer.observe(target);
    return () => observer.disconnect();
  }, [anchor]);
  if (!visible) return null;
  return (
    <button type="button" className="ay-button ay-button--dark ay-collection__top" onClick={() => anchor.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
      <Icon name="arrow-up" size={14} />
      Haut de liste
    </button>
  );
}
